import { useEffect, useId, useState } from "react";
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface ApplyConfigurationOption {
  id: string;
  name: string;
  version: string;
  description?: string | null;
  status: "published";
  isDefault?: boolean;
  languages: string[];
}

interface ApplyConfigurationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  templateLabel: string;
  warning: string;
  confirmLabel: string;
  loadTemplates: () => Promise<ApplyConfigurationOption[]>;
  requiredLanguages?: readonly string[];
  applyTemplate: (templateId: string) => Promise<unknown>;
  onApplied: (result: unknown) => Promise<void> | void;
}

function messageFromError(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

export function ApplyConfigurationModal({
  open,
  onOpenChange,
  title,
  description,
  templateLabel,
  warning,
  confirmLabel,
  loadTemplates,
  requiredLanguages,
  applyTemplate,
  onApplied,
}: ApplyConfigurationModalProps) {
  const confirmId = useId();
  const [templates, setTemplates] = useState<ApplyConfigurationOption[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>();
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [loadRequest, setLoadRequest] = useState(0);

  useEffect(() => {
    if (!open) {
      setTemplates([]);
      setSelectedTemplateId(undefined);
      setConfirmed(false);
      setLoadError(null);
      setApplyError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setApplyError(null);
    setSelectedTemplateId(undefined);
    setConfirmed(false);

    void loadTemplates()
      .then((items) => {
        if (cancelled) return;
        const expected = [...(requiredLanguages ?? [])].sort().join(",");
        setTemplates(items.filter((template) =>
          template.status === "published"
          && (
            !requiredLanguages
            || [...template.languages].sort().join(",") === expected
          )
        ));
      })
      .catch((error) => {
        if (cancelled) return;
        setTemplates([]);
        setLoadError(messageFromError(error, "Failed to load published configurations"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, loadTemplates, loadRequest, requiredLanguages]);

  const selectedTemplate = templates.find((template) => template.id === selectedTemplateId);
  const canApply = Boolean(selectedTemplate) && confirmed && !applying && !loading;

  async function applySelectedTemplate() {
    if (!selectedTemplate || !canApply) return;

    setApplying(true);
    setApplyError(null);
    try {
      const result = await applyTemplate(selectedTemplate.id);
      await onApplied(result);
      onOpenChange(false);
    } catch (error) {
      setApplyError(messageFromError(error, "Failed to apply configuration"));
    } finally {
      setApplying(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <div className="flex gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
              <div>
                <p className="font-semibold">Replaces current configuration</p>
                <p className="mt-1">
                  Applying replaces the current settings for this tab. It does not merge with
                  existing values.
                </p>
                <p className="mt-1">{warning}</p>
              </div>
            </div>
          </div>

          <div className="grid gap-2">
            <Label>{templateLabel}</Label>
            {loading ? (
              <div className="flex items-center gap-2 rounded-md border p-3 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading published configurations...
              </div>
            ) : loadError ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <p>{loadError}</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-3 bg-white"
                  onClick={() => setLoadRequest((value) => value + 1)}
                >
                  <RefreshCw className="h-4 w-4 mr-1" /> Retry
                </Button>
              </div>
            ) : templates.length === 0 ? (
              <div className="rounded-md border p-3 text-sm text-muted-foreground">
                No published configurations are available.
              </div>
            ) : (
              <Select
                value={selectedTemplateId}
                onValueChange={(value) => {
                  setSelectedTemplateId(value);
                  setConfirmed(false);
                  setApplyError(null);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a published configuration" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name} ({template.version}){template.isDefault ? " - default" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {selectedTemplate && (
            <div className="rounded-md border bg-muted/20 p-3 text-sm">
              <p className="font-medium text-foreground">
                {selectedTemplate.name} ({selectedTemplate.version})
              </p>
              {selectedTemplate.description && (
                <p className="mt-1 text-muted-foreground">{selectedTemplate.description}</p>
              )}
              <p className="mt-2 text-xs font-medium uppercase tracking-wide text-green-700">
                Published configuration
              </p>
            </div>
          )}

          <div className="flex items-start gap-2 rounded-md border p-3">
            <Checkbox
              id={confirmId}
              checked={confirmed}
              disabled={!selectedTemplate || applying}
              onCheckedChange={(checked) => setConfirmed(checked === true)}
            />
            <Label htmlFor={confirmId} className="text-sm font-normal leading-5">
              {confirmLabel}
            </Label>
          </div>

          {applyError && <p className="text-sm text-red-600">{applyError}</p>}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={applying}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={applySelectedTemplate}
            disabled={!canApply}
          >
            {applying && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {applying ? "Applying..." : "Apply configuration"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
